import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../components/CustomButton';
import EditableSection from '../../components/EditableSection';
import SelectField from '../../components/ui/SelectField';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useGetHolidayStatusQuery, useUpdateHolidayRegionMutation } from '../../api/account/accountApiSlice';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';
import { formatDate } from '../../utils';
import { roomUseCasesOf } from '../../utils/roomUseCases';
import { stationCalendarDay } from '../../utils/stationTime';
import HolidaySourceCredit from './HolidaySourceCredit';

interface Props {
    readOnly: boolean;
    /** `tenant.config.holidayRegion` — null means unset. */
    holidayRegion: string | null;
    /** The tenant's active use cases; holidays serve the room family's series. */
    services: string[] | null;
    /** Master viewing another tenant: write that tenant by slug. */
    masterSlug?: string;
}

/**
 * The holiday region (M9, #3041; serial-use plan §0.3 D1): the German state
 * whose public and school holidays apply. While none is set, no holiday is
 * looked up at all (PO, 2026-10-23), and this section says so rather than
 * reading as "no holidays".
 *
 * One editable unit (design-decisions › Bearbeiten und Speichern). Below it,
 * the data's version and reach and the source credit the licence asks for.
 */
const HolidayRegionSection = ({ readOnly, holidayRegion, services, masterSlug }: Readonly<Props>) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [updateHolidayRegion, { isLoading: saving }] = useUpdateHolidayRegionMutation();
    const { data: status, isError: statusFailed } = useGetHolidayStatusQuery();
    const draft = useEditableDraft<{ region: string | null }>({ region: holidayRegion });

    if (roomUseCasesOf(services).length === 0) return null;

    const language = i18n.language === 'en' ? 'en' : 'de';
    const regions = status?.regions ?? [];
    const regionLabels: Record<string, string> = Object.fromEntries(
        regions.map((r) => [r, String(t(`services.holiday_region.regions.${r}`))]),
    );
    const { region } = draft.value;

    const handleSave = async () => {
        try {
            await updateHolidayRegion({ holidayRegion: region, slug: masterSlug }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('services.holiday_region.saved'), autoHideDuration: 3000 }));
            draft.finish();
        } catch (err) {
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(err, t('services.holiday_region.error'), t),
                autoHideDuration: 5000,
            }));
        }
    };

    const dataLine = (() => {
        if (statusFailed) return t('services.holiday_region.data_failed');
        if (!status?.data) return null;
        if (!status.data.importedAt || !status.data.coversUntil) return t('services.holiday_region.data_missing');
        return t('services.holiday_region.data_line', {
            loaded: formatDate(stationCalendarDay(status.data.importedAt), language),
            until: formatDate(status.data.coversUntil, language),
        });
    })();

    // From the draft, not the prop: right after saving, the draft holds the
    // saved value until the account refetch lands (as SeriesSeasonSection does).
    const view = (
        <div className={'flex flex-col gap-2 text-sm'}>
            <p className={'text-foreground'}>
                {region ? regionLabels[region] ?? region : t('services.holiday_region.unset')}
            </p>
            {dataLine && <p className={'text-foreground/60'}>{dataLine}</p>}
            {status?.credit && <HolidaySourceCredit credit={status.credit} />}
        </div>
    );

    return (
        <EditableSection
            id={'holiday-region'}
            title={t('services.holiday_region.section')}
            description={t('services.holiday_region.section_desc')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            canSave
            onEdit={draft.startEdit}
            onCancel={draft.cancel}
            onSave={() => { void handleSave(); }}
            view={view}
        >
            <SelectField
                label={t('services.holiday_region.label')}
                value={region ?? ''}
                onChange={(value) => draft.setDraft({ region: value || null })}
                options={regions}
                optionLabels={regionLabels}
                placeholder={t('services.holiday_region.placeholder')}
                noOptions={statusFailed ? t('services.holiday_region.data_failed') : undefined}
                disabled={saving}
                labelAbove
            />
            {!region && <p className={'text-sm text-foreground'}>{t('services.holiday_region.unset')}</p>}
            {region && (
                <div>
                    <CustomButton
                        title={t('services.holiday_region.clear')}
                        isFilled={false}
                        onClick={() => draft.setDraft({ region: null })}
                        disabled={saving}
                    />
                </div>
            )}
        </EditableSection>
    );
};

export default HolidayRegionSection;
